import { motion } from "framer-motion";
import { GlassCard, ScreenShell, SimHeader } from "./shared";
import { RippleStrip } from "./decor";

export type OutcomeBand = "strong" | "developing" | "needs-work";

export interface OutcomeData {
  decisionNumber: number;
  totalDecisions: number;
  band: OutcomeBand;
  scorePct: number;
  choiceLabel: string;
  consequence: string;
  isAiWritten?: boolean;
  isLast?: boolean;
}

const TOKENS: Record<OutcomeBand, { color:string; soft:string; label:string; emoji:string; subtitle:string }> = {
  strong:        { color:"#10B981", soft:"linear-gradient(135deg, rgba(16,185,129,0.1), rgba(6,182,212,0.07))",    label:"Strong Response", emoji:"⚡", subtitle:"You responded with clarity and depth" },
  developing:    { color:"#F59E0B", soft:"linear-gradient(135deg, rgba(245,158,11,0.1), rgba(232,121,249,0.06))",  label:"Developing",      emoji:"📈", subtitle:"There's a stronger move available here" },
  "needs-work":  { color:"#F87272", soft:"linear-gradient(135deg, rgba(248,114,114,0.1), rgba(139,92,246,0.06))", label:"Needs Work",      emoji:"🔄", subtitle:"Let's unpack what this choice signals" },
};

export function OutcomeScreen({
  data,
  xp = 0,
  timerDisplay,
  onContinue,
}: {
  data: OutcomeData;
  xp?: number;
  timerDisplay?: string;
  onContinue?: () => void;
}) {
  const t = TOKENS[data.band];
  const progress = (data.decisionNumber / data.totalDecisions) * 100;
  const ringCirc = 2 * Math.PI * 28;
  const dash = (data.scorePct / 100) * ringCirc;

  return (
    <ScreenShell>
      <SimHeader decision={data.decisionNumber} xp={xp} timerDisplay={timerDisplay} />
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-4 pb-12 md:grid-cols-2 md:px-8">
        {/* LEFT */}
        <div className="flex flex-col gap-4">
          <div className="h-[3px] w-full overflow-hidden rounded-full bg-white/5">
            <div className="h-full transition-all duration-700"
              style={{ width:`${progress}%`, background:`linear-gradient(90deg, ${t.color}, #22D3EE)` }} />
          </div>
          <motion.div className="rounded-2xl border p-6" style={{ background:t.soft, borderColor:`${t.color}47` }}
            initial={{ y:20, opacity:0 }} animate={{ y:0, opacity:1 }}
            transition={{ type:"spring", stiffness:280, damping:22 }}>
            <div className="flex items-center gap-4">
              <svg width="64" height="64" viewBox="0 0 64 64">
                <circle cx="32" cy="32" r="28" stroke="rgba(255,255,255,0.08)" strokeWidth="6" fill="none" />
                <motion.circle cx="32" cy="32" r="28" stroke={t.color} strokeWidth="6" fill="none"
                  strokeLinecap="round" transform="rotate(-90 32 32)"
                  strokeDasharray={ringCirc} initial={{ strokeDashoffset:ringCirc }}
                  animate={{ strokeDashoffset:ringCirc-dash }} transition={{ duration:1, ease:[0.16,1,0.3,1] }} />
              </svg>
              <div>
                <div className="flex items-center gap-2 text-[20px] font-bold" style={{ color:t.color }}>
                  <motion.span initial={{ scale:0 }} animate={{ scale:[0,1.2,1] }}
                    transition={{ duration:0.5, times:[0,0.6,1], ease:"easeOut", delay:0.2 }} className="inline-block">
                    {t.emoji}
                  </motion.span>
                  {t.label}
                </div>
                <div className="text-[14px] text-white/55">{t.subtitle}</div>
              </div>
            </div>
          </motion.div>

          <div className="mt-2 text-[10px] font-bold uppercase tracking-[0.2em] text-white/30">Your Choice</div>
          <GlassCard className="p-5">
            <div className="text-[13px] leading-[1.65]" style={{ color:"#CBD5E1" }}>{data.choiceLabel}</div>
          </GlassCard>
        </div>

        {/* RIGHT */}
        <div className="flex flex-col gap-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color:"#F59E0B" }}>💡 What Happens Next</div>
          <GlassCard className="relative overflow-hidden p-6">
            {data.band === "strong" && <RippleDecor />}
            <div className="relative text-[14px] leading-[1.65]" style={{ color:"rgba(255,255,255,0.85)" }}>
              {data.consequence}
            </div>
            {data.isAiWritten && (
              <p className="mt-3 text-xs text-amber-300/80">⚠ This response looked AI-generated — try it in your own voice next time.</p>
            )}
          </GlassCard>
          <div className="mt-2"><RippleStrip color={t.color} /></div>
          <button onClick={onContinue}
            className="cta-gradient relative mt-2 overflow-hidden rounded-2xl py-4 text-[15px] font-bold text-white">
            <span className="relative z-10">{data.isLast ? "Complete simulation →" : "Continue to next decision →"}</span>
            <span className="pointer-events-none absolute inset-0"
              style={{ background:"linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)", backgroundSize:"200% 100%", animation:"shimmer 2.4s linear infinite" }} />
          </button>
        </div>
      </div>
    </ScreenShell>
  );
}

function RippleDecor() {
  return (
    <svg className="pointer-events-none absolute -top-6 -right-6 h-32 w-32" viewBox="0 0 100 100" aria-hidden>
      {[18,30,42].map((r,i) => (
        <motion.circle key={r} cx="60" cy="40" r={r} fill="none" stroke="#10B981" strokeWidth="1"
          initial={{ opacity:0, scale:0.4 }} animate={{ opacity:[0,0.5,0], scale:[0.4,1,1.3] }}
          transition={{ duration:3, repeat:Infinity, delay:i*0.6, ease:"easeOut" }}
          style={{ transformOrigin:"60px 40px" }} />
      ))}
    </svg>
  );
}
